// Foundations
import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Output,
  EventEmitter,
  Input,
} from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';

// rxjs
import { finalize, map } from 'rxjs/operators';

// Services
import { GetNearestVetsGQL } from '@app/services/graphql.service';
import { LoadingIndicatorService } from '@app/services/loading-indicator.service';
import { ParsedVet } from '@app/models/pet/vet.model';

// Models

const POSTAL_CODE_REGEX =
  /^(\d{5}(\-\d{4})?|[A-Za-z]\d[A-Za-z][ \-]?\d[A-Za-z]\d)$/;

@Component({
  selector: 'app-vet-location-search-select',
  templateUrl: './vet-location-search-select.component.html',
  styleUrls: ['./vet-location-search-select.component.scss'],
})
export class VetLocationSearchSelectComponent implements OnInit, AfterViewInit {
  @Output() selectedVetId = new EventEmitter<string>();

  @ViewChild('postalCode', { static: false }) postalCode: ElementRef;

  form: FormGroup;

  showResults = false;
  displayInput = '';
  currentSelection: ParsedVet;

  radioOptions: Partial<ParsedVet>[];

  constructor(
    private fb: FormBuilder,
    private getNearestVetGQL: GetNearestVetsGQL,
    private loadingIndicatorService: LoadingIndicatorService
  ) {
    this.form = this.fb.group(
      {
        postalCode: [''],
      },
      { validator: this.checkPostalCodePattern }
    );
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.postalCode.nativeElement.focus();
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  get f(): any {
    return this.form.controls;
  }

  changeCheckedRadio(vet: ParsedVet): void {
    vet.selected = !vet.selected;

    if (!vet.selected) {
      this.clearCurrentSelection();
      return;
    }

    this.currentSelection = vet;
    this.selectedVetId.emit(this.currentSelection.id);

    // uncheck all other options
    this.radioOptions.forEach((option) => {
      if (option.id !== vet.id) {
        option.selected = false;
      }
    });
  }

  onSearch(): void {
    this.postalCode.nativeElement.blur();
    this.clearCurrentSelection();

    if (!this.form.hasError('postalCode')) {
      this.showResults = true;
      this.displayInput = this.form.controls.postalCode.value;
      this.loadingIndicatorService.show();
      this.fetchData();
    } else {
      this.showResults = false;
    }
  }

  clearCurrentSelection(): void {
    this.currentSelection = null;
    this.selectedVetId.emit(null);
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private fetchData(): void {
    this.getNearestVetGQL
      .fetch({ code: this.form.controls.postalCode.value })
      .pipe(
        map((res) => res.data.getNearestVets),
        finalize(() => this.loadingIndicatorService.hide())
      )
      .subscribe((vets) => {
        this.radioOptions = [];
        let NAVet;
        if (Array.isArray(vets)) {
          /*
            In the list of "vet", there is a "N/A" option which has "0" as
            `latitude` and `longitude`.
          */
          vets.forEach((vet) => {
            //
            if (vet.latitude === 0 && vet.longitude === 0) {
              NAVet = {
                id: vet.id,
                practiceName: 'My vet is not listed',
                latitude: vet.latitude,
                longitude: vet.longitude,
                distanceInMi: null,
              };

              // Ignore this one
              return;
            }

            // Use the "first" phone
            let vetPhone;
            if (Array.isArray(vet.phones) && vet.phones.length > 0) {
              vetPhone = vet.phones[0].phoneNumber;
            }

            vet.address2 = vet.address2 ? vet.address2.trim() : vet.address2;

            //
            const {
              id,
              practiceName,
              address1,
              address2,
              city,
              state,
              zipcode,
              email,
              distanceInMi,
              distanceInKm,
              latitude,
              longitude,
            } = vet;
            this.radioOptions.push({
              id,
              practiceName,
              address1,
              address2,
              city,
              state,
              zipcode,
              email,
              distanceInMi,
              distanceInKm,
              telephone: vetPhone,
              latitude,
              longitude,
            });
          });
        }

        // Add "N/A" option if needed
        if (NAVet) {
          this.radioOptions.unshift(NAVet);
        }
      });
  }

  private checkPostalCodePattern(group: FormGroup): { [k: string]: any } {
    const errors: { [k: string]: any } = {};
    const postalCode = group.controls.postalCode.value;

    if (!POSTAL_CODE_REGEX.test(postalCode)) {
      errors.postalCode = true;
    }

    return errors;
  }
}
